import React from 'react';
import HeroPlaceholder from '../components/HeroPlaceholder';
import Timeline from '../components/Timeline';
import CtaBand from '../components/CtaBand';
import SEO from '../components/SEO';
import { HERO_MEDIA } from '../config/media';
import { ABOUT_HUB } from '../data/copy';
import { usePageContent } from '../lib/useContent';
import {
    orgJsonLd,
    aboutPageJsonLd,
    breadcrumbJsonLd,
} from '../lib/seo';

// Frozen inline fallback - matches the current page content verbatim so
// the public page renders identically if the CMS fetch fails or returns
// nothing. Phase 2.5 wires `usePageContent('about', ABOUT_FALLBACK)`;
// when the CMS reply arrives it shallow-merges over this fallback.
const ABOUT_FALLBACK = {
    hero: {
        eyebrow: ABOUT_HUB.eyebrow,
        headline: ABOUT_HUB.title,
        body: 'Founded 1955 in coastal British Columbia. Still family-owned. Still milling our own mats.',
        image_url: HERO_MEDIA.millYard.src,
    },
    story: {
        lead: ABOUT_HUB.lead,
        paragraphs: ABOUT_HUB.intro,
    },
};

export default function About() {
    const content = usePageContent('about', ABOUT_FALLBACK);
    const hero = content.hero || ABOUT_FALLBACK.hero;
    const story = content.story || ABOUT_FALLBACK.story;

    return (
        <>
            <SEO
                title="About - Family-Owned Since 1955"
                description="Centurion Matting Products - third-generation family-owned BC manufacturer of timber access, rig and crane mats. Multiple facilities across Western Canada. Manufacturer Direct since 1955."
                cmsSlug="seo.about"
                path="/about"
                image={HERO_MEDIA.millYard.src}
                jsonLd={[
                    orgJsonLd(),
                    aboutPageJsonLd(),
                    breadcrumbJsonLd([
                        { name: 'Home', path: '/' },
                        { name: 'About', path: '/about' },
                    ]),
                ]}
            />

            <HeroPlaceholder
                mediaKey="millYard"
                imageUrl={hero.image_url}
                eyebrow={hero.eyebrow}
                title={hero.headline}
                subtitle={hero.body}
            />

            {/* Authentic verbatim lead paragraph */}
            <section className="mx-auto max-w-4xl px-6 py-20 md:py-28">
                <span className="eyebrow">Our Story</span>
                <p
                    data-testid="about-lead"
                    className="mt-8 font-sans text-lg leading-relaxed text-brand-text"
                >
                    {story.lead}
                </p>
                <div className="mt-10 space-y-6 font-sans text-base leading-relaxed text-brand-muted">
                    {(story.paragraphs || []).map((p) => (
                        <p key={p.slice(0, 60)}>{p}</p>
                    ))}
                </div>
            </section>

            {/* Mill yard image as a section break */}
            <section
                className="relative h-[280px] md:h-[360px] overflow-hidden border-y border-brand-hairline"
                aria-hidden="true"
            >
                <img
                    src={HERO_MEDIA.millYard.src}
                    alt={HERO_MEDIA.millYard.alt}
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            'linear-gradient(to bottom, #0B0B0D 0%, transparent 40%, #0B0B0D 100%)',
                        opacity: 0.6,
                    }}
                />
            </section>

            <section className="border-y border-brand-hairline bg-brand-surface">
                <div className="mx-auto max-w-4xl px-6 py-20 md:py-28">
                    <span className="eyebrow">Timeline (Illustrative)</span>
                    <h2 className="h-display mt-6 text-3xl sm:text-4xl">
                        Three generations of milling.
                    </h2>
                    <p className="mt-5 font-sans text-sm text-brand-muted">
                        1955 founding is documented; later milestones reflect a
                        multi-generation family operation.
                    </p>
                    <div className="mt-10">
                        <Timeline />
                    </div>
                </div>
            </section>

            <CtaBand
                eyebrow="Manufacturer Direct"
                title="Pricing and lead times come from the mill."
                cta={{ to: '/quote', label: 'Get a Quote' }}
                secondary={{ to: '/contact', label: 'Contact Centurion' }}
            />
        </>
    );
}
